/**
 * Atlas PM · 系统设置 · 任务模板管理面板（P8.2）。
 *
 * 可见范围（后端保证）：
 *   · 超级管理员 → 全部模板（含所有人的个人模板）
 *   · 普通用户   → 自己的个人模板 + 全部公共模板
 *
 * 能力：搜索（名称）+ 范围筛选（全部/个人/公共）+ 状态筛选（全部/启用/停用）
 *       + 编辑基本信息 + 删除（二次确认）。
 *
 * ⚠️ 本轮不支持在设置页手工「新建模板」——模板只能由任务详情「存为模板」生成
 *    （结构快照来自真实任务，避免手工造出无指标/无要素的空模板）。详见空态提示。
 */

import { useEffect, useMemo, useState } from 'react';
import Badge from '../../../components/Badge';
import Button from '../../../components/Button';
import EmptyState from '../../../components/EmptyState';
import Panel from '../../../components/Panel';
import Spinner from '../../../components/Spinner';
import { useAuthStore } from '../../../stores/auth';
import DeleteTaskTemplateDialog from './DeleteTaskTemplateDialog';
import TaskTemplateDialog from './TaskTemplateDialog';
import { type TaskTemplateItem, type TemplateScope } from './api';
import { useTaskTemplateList } from './useTaskTemplates';
import './task-templates.css';

const PAGE_SIZE = 20;

type ScopeFilter = 'all' | TemplateScope;
type StatusFilter = 'all' | 'active' | 'inactive';

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(
    d.getMinutes(),
  )}`;
}

export default function TaskTemplatesPanel() {
  const me = useAuthStore((s) => s.user);
  const canManage = useAuthStore((s) => s.hasPermission('template:manage'));

  const [page, setPage] = useState(1);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [editing, setEditing] = useState<TaskTemplateItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TaskTemplateItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // 搜索防抖 300ms → 生效并回到第一页
  useEffect(() => {
    const t = setTimeout(() => {
      setKeyword(keywordInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [keywordInput]);

  // 成功提示自动消失
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3600);
    return () => clearTimeout(t);
  }, [notice]);

  const params = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      keyword: keyword || undefined,
      scope: scopeFilter === 'all' ? undefined : scopeFilter,
      // 状态：全部 → includeInactive；启用/停用 → 显式 isActive
      isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      includeInactive: statusFilter === 'all' ? true : undefined,
    }),
    [page, keyword, scopeFilter, statusFilter],
  );

  const listQ = useTaskTemplateList(params);

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // 编辑 / 删除按钮可见性：个人模板 = 本人创建 或 可管理公共者；公共模板 = 可管理公共者
  const canModify = (t: TaskTemplateItem): boolean =>
    t.scope === 'PUBLIC'
      ? canManage
      : t.createdById === me?.userId || canManage;

  const resetToFirstPage = () => setPage(1);

  return (
    <Panel
      title="任务模板"
      extra={
        <span className="tpl-count">
          {listQ.isFetching && <span className="tpl-count__dot" aria-hidden />}
          共 <b>{total}</b> 个模板
        </span>
      }
      flush
    >
      <div className="tpl-toolbar">
        <div className="tpl-search">
          <span className="tpl-search__icon">⌕</span>
          <input
            className="tpl-search__input"
            data-testid="tpl-search"
            value={keywordInput}
            placeholder="搜索模板名称"
            onChange={(e) => setKeywordInput(e.target.value)}
          />
          {keywordInput && (
            <button
              type="button"
              className="tpl-search__clear"
              aria-label="清空搜索"
              onClick={() => setKeywordInput('')}
            >
              ✕
            </button>
          )}
        </div>

        <label className="tpl-filter">
          <span className="tpl-filter__label">范围</span>
          <select
            data-testid="tpl-filter-scope"
            className="tpl-filter__select"
            value={scopeFilter}
            onChange={(e) => {
              setScopeFilter(e.target.value as ScopeFilter);
              resetToFirstPage();
            }}
          >
            <option value="all">全部范围</option>
            <option value="PERSONAL">个人</option>
            <option value="PUBLIC">公共</option>
          </select>
        </label>

        <label className="tpl-filter">
          <span className="tpl-filter__label">状态</span>
          <select
            data-testid="tpl-filter-status"
            className="tpl-filter__select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              resetToFirstPage();
            }}
          >
            <option value="all">全部状态</option>
            <option value="active">仅启用</option>
            <option value="inactive">仅停用</option>
          </select>
        </label>
      </div>

      {notice && (
        <div className="tpl-notice" role="status" data-testid="tpl-notice">
          <span className="tpl-notice__icon">✓</span>
          {notice}
        </div>
      )}

      {listQ.isLoading ? (
        <Spinner label="加载任务模板…" />
      ) : listQ.isError ? (
        <div className="tpl-error" data-testid="tpl-error">
          ⚠ {listQ.error instanceof Error ? listQ.error.message : '任务模板加载失败'}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="⧉"
          title={keyword ? `没有匹配「${keyword}」的模板` : '暂无模板'}
          hint="模板由任务详情页「存为模板」生成；生成后可在本页编辑基本信息或删除。"
        />
      ) : (
        <>
          <div className="pm-table-wrap">
            <table className="pm-table" data-testid="tpl-table">
              <thead>
                <tr>
                  <th>模板名称</th>
                  <th>范围</th>
                  <th>指标 · 要素</th>
                  <th>创建人</th>
                  <th>状态</th>
                  <th>更新时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => {
                  const modifiable = canModify(t);
                  return (
                    <tr key={t.id} data-testid="tpl-row" data-template-id={t.id}>
                      <td className="pm-cell--name">
                        <div className="tpl-name" data-testid="tpl-name">
                          {t.name}
                        </div>
                        {t.description && <div className="tpl-desc">{t.description}</div>}
                      </td>
                      <td>
                        {t.scope === 'PUBLIC' ? (
                          <Badge tone="brand">公共</Badge>
                        ) : (
                          <Badge tone="idle">个人</Badge>
                        )}
                      </td>
                      <td className="num">
                        {t.metricCount} · {t.elementCount}
                      </td>
                      <td>{t.createdByName ?? '—'}</td>
                      <td>
                        {t.isActive ? <Badge tone="ok">启用</Badge> : <Badge tone="idle">停用</Badge>}
                      </td>
                      <td className="num">{fmtDateTime(t.updatedAt)}</td>
                      <td>
                        <div className="pm-actions">
                          <button
                            type="button"
                            className="pm-linkbtn"
                            data-testid="tpl-edit"
                            disabled={!modifiable}
                            title={modifiable ? '编辑基本信息' : '无权编辑该模板'}
                            onClick={() => setEditing(t)}
                          >
                            编辑
                          </button>
                          <button
                            type="button"
                            className="pm-linkbtn is-danger"
                            data-testid="tpl-delete"
                            disabled={!modifiable}
                            title={modifiable ? '删除模板' : '无权删除该模板'}
                            onClick={() => setPendingDelete(t)}
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="tpl-pager">
            <span className="tpl-pager__info">
              第 {page} / {totalPages} 页 · 共 {total} 条
            </span>
            <div className="tpl-pager__btns">
              <Button
                size="sm"
                variant="secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                下一页
              </Button>
            </div>
          </div>
        </>
      )}

      <TaskTemplateDialog
        open={editing !== null}
        template={editing}
        onClose={() => setEditing(null)}
        onSaved={() => setNotice('模板已更新')}
      />

      <DeleteTaskTemplateDialog
        open={pendingDelete !== null}
        template={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDeleted={() => setNotice('模板已删除')}
      />
    </Panel>
  );
}
